import { formatNumber } from "@angular/common";
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, Output, SimpleChanges } from "@angular/core";
import { ChartConfiguration, Plugin } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { abbreviate, FONT_FAMILY, GRID, INK, SLATE, fitLabel } from "../../charts/bar-chart/bar-chart.component";
import { getInsightsWithoutCreate, Linqset } from "../../common/linqset-model";
import { DEFAULT_CURRENCY, getCurrency } from "../../common/organisation";
import { colorAsARGB } from "../../model-services/model-item-enums";
import { OrganisationService } from "../../organisation/organisation.service";
import { InsightsFieldType } from "../model/insights-field-type";
import { NODE_TYPE_DATA_TYPE, NodeTypeData } from "../node-types/node-type-data";
import { ParetoRow, paretoRows } from "./node-chart-data";

const CUT = 0.8;

// a dashed line under the last of the few that make up 80% of the total
const cutLine = (cutAfter: () => number): Plugin => ({
    id: 'paretoCut',
    afterDatasetsDraw(chart) {
        const after = cutAfter();
        const meta = chart.getDatasetMeta(0);
        const bar: any = meta?.data?.[after];
        const next: any = meta?.data?.[after + 1];
        if (!bar || !next) return;
        const y = (bar.y + next.y) / 2;
        const area = chart.chartArea;
        const ctx = chart.ctx;
        ctx.save();
        ctx.strokeStyle = INK;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(area.left, y);
        ctx.lineTo(area.right, y);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = INK;
        ctx.font = `600 11px ${FONT_FAMILY}`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        ctx.fillText(`${Math.round(CUT * 100)}% of the total`, area.right, y - 2);
        ctx.restore();
    },
});

// the largest first, with how much of the total each brings the running total to, and a line where the few that make
// up 80% of it end: where to look first. The running total is in the labels, not on a second axis
@Component({
    selector: 'app-node-pareto-chart',
    templateUrl: './node-pareto.html',
    styleUrls: ['./node-charts.css'],
    standalone: false
})
export class NodeParetoComponent implements OnChanges {
    @Input() typeData: NodeTypeData
    @Input() fieldType: InsightsFieldType
    @Input() trigger: any
    @Input() topNumber: number
    @Input() linqset: Linqset

    @Output() selectNodeEvent = new EventEmitter<string>();

    rows: ParetoRow[] = []
    shown: ParetoRow[] = []
    coreCount = 0
    count = 0
    total = 0
    datasets: any[] = []
    labels: string[] = []
    options: ChartConfiguration['options'] = {}
    plugins = [ChartDataLabels, cutLine(() => this.coreCount - 1)]

    constructor(@Inject(LOCALE_ID) private locale: string, private organisationService: OrganisationService, private changeDetectorRef: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['trigger'] || changes['typeData'] || changes['fieldType'] || changes['topNumber']) this.populate();
    }

    get height() {
        return Math.max(220, Math.min(1100, this.shown.length * 26 + 90));
    }

    get fieldName() {
        return (this.fieldType?.getTitle(this.typeData.dataType, this.typeData.superType) || '').toLowerCase();
    }

    // a value as the chart shows it: shortened, and in the organisation's currency when it is money
    value(v: number, short = true) {
        const symbol = this.fieldType?.isCurrency() ? getCurrency(this.organisationService.organisation?.currencyCode || DEFAULT_CURRENCY).symbol : '';
        const units = !symbol && this.fieldType?.getUnits() ? ' ' + this.fieldType.getUnits() : '';
        return symbol + (short ? abbreviate(v, this.locale) : formatNumber(Math.round(v), this.locale, '1.0-0')) + (short ? '' : units);
    }

    percent(v: number) {
        return `${Math.round(v * 100)}%`;
    }

    private items() {
        const t = this.typeData, f = this.fieldType;
        if (t.dataType === NODE_TYPE_DATA_TYPE.MODELS) {
            return (t.models || []).map(m => ({ key: m._key, name: m.name, value: f.getSingleValueFromInsights(m.insights.find(i => i.projectKey === f.workspaceKey), t.dataType) || 0 }));
        }
        if (t.dataType === NODE_TYPE_DATA_TYPE.GROUP) {
            return (t.groups || []).filter(g => g.insights).map((g, i) => ({ key: g._key, name: g.name || 'Missing name ' + i, value: f.getSingleValueFromInsights(g.insights, t.dataType) || 0 }));
        }
        // a node's own insights in the view: in the whole organisation, actions and information keep those of their model
        return (t.list || []).filter(n => !n.excluded).map((n, i) => ({ key: n._key, name: n.name || 'Missing name ' + i, value: f.getSingleValueFromInsights(getInsightsWithoutCreate(n, this.linqset), t.dataType) || 0 }));
    }

    private populate() {
        if (!this.typeData || !this.fieldType?.isValid()) return;
        const result = paretoRows(this.items(), CUT);
        this.rows = result.rows;
        this.coreCount = result.coreCount;
        this.count = result.count;
        this.total = result.total;
        // at least the few that make up 80%, so the line is always in view
        this.shown = this.rows.slice(0, Math.max(this.topNumber || 35, this.coreCount + 1));

        const colour = this.typeData.colour || '#5b6172';
        const rgb = colorAsARGB(colour);
        const faded = `rgba(${rgb.r},${rgb.g},${rgb.b},0.35)`;
        this.labels = this.shown.map(r => r.name);
        this.datasets = [{
            label: this.fieldType.type,
            data: this.shown.map(r => r.value),
            backgroundColor: this.shown.map(r => r.core ? colour : faded),
            hoverBackgroundColor: this.shown.map(r => r.core ? (this.typeData.hoverColour || colour) : colour),
            borderRadius: 4, borderSkipped: false, maxBarThickness: 22,
        }];
        this.options = this.buildOptions();
        this.changeDetectorRef.markForCheck();
    }

    private buildOptions(): ChartConfiguration['options'] {
        return <any>{
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            layout: { padding: { right: 96 } },
            scales: {
                x: {
                    beginAtZero: true,
                    grid: { color: GRID, drawTicks: false }, border: { display: false },
                    ticks: { color: SLATE, padding: 6, font: { family: FONT_FAMILY, size: 11 }, callback: (v: any) => this.value(Number(v)) },
                    title: this.fieldType.getUnits() ? { display: true, text: this.fieldType.getUnits(), color: SLATE, font: { family: FONT_FAMILY, size: 12, weight: 600 }, padding: { top: 8 } } : { display: false },
                },
                y: {
                    grid: { display: false }, border: { color: '#d5d9e2' },
                    ticks: {
                        color: INK, autoSkip: false, padding: 6, font: { family: FONT_FAMILY, size: 12 },
                        callback: function (this: any, v: any) { return fitLabel(this.chart, String(this.getLabelForValue(Number(v)) ?? '')); },
                    },
                },
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: INK, padding: 10, cornerRadius: 6,
                    titleFont: { family: FONT_FAMILY, size: 12, weight: 600 }, bodyFont: { family: FONT_FAMILY, size: 12 },
                    callbacks: {
                        label: (c: any) => {
                            const r = this.shown[c.dataIndex];
                            return r ? `${this.value(r.value, false)}: ${this.percent(r.share)} of the total` : '';
                        },
                        afterLabel: (c: any) => {
                            const r = this.shown[c.dataIndex];
                            return r ? `With all those above it: ${this.percent(r.cumulative)}` : '';
                        },
                    },
                },
                // the value, then the running total it brings it to
                datalabels: {
                    color: INK, anchor: 'end', align: 'end', offset: 4, clamp: true, clip: false, display: 'auto',
                    font: { family: FONT_FAMILY, size: 11, weight: 600 },
                    formatter: (v: number, c: any) => {
                        const r = this.shown[c.dataIndex];
                        return r ? `${this.value(r.value)} · ${this.percent(r.cumulative)}` : '';
                    },
                },
            },
        };
    }

    onClick(event: any) {
        const index = event?.active?.[0]?.index;
        if (index !== undefined && this.shown[index]) this.selectNodeEvent.emit(this.shown[index].key);
    }
}
